import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const Meta = ({ title, description, image, keywords, author, type = 'website' }) => {
  const location = useLocation();
  const defaultTitle = 'Swytrix Technologies LLC - United States | Enterprise-Grade Software Development';
  const defaultDescription =
    'Swytrix Technologies LLC United States: Web/Mobile Development, Cloud Solutions, DevOps, Cybersecurity, AI/ML, and 24×7 IT Support. Enterprise-grade software with same-day kickoff.';
  const defaultImage = 'https://swytrixtech.com/og-image.jpg';
  const defaultUrl = `https://swytrixtech.com${location.pathname}`;
  const defaultKeywords = 'IT Solutions United States, Web Development US, Software Development United States, Cloud Migration, DevOps, Cybersecurity, AI ML Solutions, ERP CRM United States, E-commerce Development, Mobile App Development United States';
  const defaultAuthor = 'Swytrix Technologies LLC';

  useEffect(() => {
    document.title = title || defaultTitle;
    
    const updateMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateProperty = (property, content) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateLink = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Basic Meta Tags
    updateMeta('description', description || defaultDescription);
    updateMeta('keywords', keywords || defaultKeywords);
    updateMeta('author', author || defaultAuthor);
    updateMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    updateMeta('googlebot', 'index, follow');
    updateMeta('language', 'English');
    updateMeta('revisit-after', '7 days');
    updateMeta('distribution', 'global');
    updateMeta('rating', 'general');

    // Open Graph Tags
    updateProperty('og:title', title || defaultTitle);
    updateProperty('og:description', description || defaultDescription);
    updateProperty('og:image', image || defaultImage);
    updateProperty('og:url', defaultUrl);
    updateProperty('og:type', type);
    updateProperty('og:site_name', 'Swytrix Technologies LLC');
    updateProperty('og:locale', 'en_US');
    updateProperty('og:locale:alternate', 'ar_AE');

    // Twitter Card Tags
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', title || defaultTitle);
    updateMeta('twitter:description', description || defaultDescription);
    updateMeta('twitter:image', image || defaultImage);
    updateMeta('twitter:site', '@swytrixtech');
    updateMeta('twitter:creator', '@swytrixtech');

    // Canonical URL
    updateLink('canonical', defaultUrl);

    // Alternate Language
    updateLink('alternate', `https://swytrixtech.com/ar${location.pathname}`);
    updateLink('alternate', `https://swytrixtech.com${location.pathname}`);

    // Structured Data (JSON-LD)
    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Swytrix Technologies LLC',
      url: 'https://swytrixtech.com',
      logo: 'https://swytrixtech.com/logo.png',
      description: defaultDescription,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Business Bay',
        addressLocality: 'United States',
        addressRegion: 'United States',
        postalCode: '00000',
        addressCountry: 'AE',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '25.2048',
        longitude: '55.2708',
      },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+971-55-123-4567',
          contactType: 'Customer Service',
          email: 'info@swytrixtech.com',
          areaServed: 'AE',
          availableLanguage: ['English', 'Hindi'],
        },
        {
          '@type': 'ContactPoint',
          telephone: '+971-55-123-4567',
          contactType: 'Sales',
          email: 'sales@swytrixtech.com',
          areaServed: 'AE',
        },
        {
          '@type': 'ContactPoint',
          telephone: '+971-55-123-4567',
          contactType: 'Technical Support',
          email: 'support@swytrixtech.com',
          areaServed: 'AE',
          availableLanguage: ['English', 'Hindi'],
        },
      ],
      sameAs: [
        // 'https://www.linkedin.com/company/sandy-it-solution',
        // 'https://twitter.com/swytrixtech',
        // 'https://www.facebook.com/swytrixtech',
        // 'https://www.instagram.com/swytrixtech',
        // 'https://www.youtube.com/@swytrixtech',
      ],
      foundingDate: '2020',
      numberOfEmployees: {
        '@type': 'QuantitativeValue',
        value: '50+',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '127',
        bestRating: '5',
        worstRating: '1',
      },
    });
  }, [title, description, image, keywords, author, type, location.pathname, defaultTitle, defaultDescription, defaultImage, defaultUrl, defaultKeywords, defaultAuthor]);

  return null;
};

export default Meta;

